// The channel details dialog (CHAT-CONTROLS; Slack's, opened from the channel's name in the
// header, its "…" and the sidebar row's "…"): About (name, description, created by and when,
// Leave), Members (list with the DE badge, "Add people" across people and DEs, remove, make or
// unmake a channel admin), Settings (who posts, who opens topics, private, the default
// notification level, and CHAT-DE's one "Digital employees" row: the agents' wake rule and
// standing instructions — the Agents tab it replaces opens Settings) and, for the channel's managers, a
// Danger zone (archive or restore, delete with the name typed back). Every control is a
// visible button; the managers' ones show to managers only (`useCanManage`: the API's
// `access.manage`); the server refuses the rest anyway (06: permission-filtered server-side).
// PARITY-0: the tabs are a registry (`registerChannelDetailsTab`); the dialog draws the ones
// whose `when` holds, in order.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  InfoTip,
  Input,
  Segmented,
  Switch,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  LogOutIcon,
  ShieldCheckIcon,
  ShieldOffIcon,
  Trash2Icon,
  UserMinusIcon,
} from "lucide-react";
import { type ComponentType, type FormEvent, useState } from "react";
import { orderedRegistry } from "../../extensions/ordered-registry";
import { keepFocus } from "../../focus/focus";
import { CharterDialog } from "../memory/people-cards";
import { featureState, useSession } from "../use-feature";
import { AddPeopleButton, useManagesChannel } from "./channel-agents";
import { AgentRulesRow, useCanManage, usePrefs } from "./channel-settings";
import { SharingTab } from "./channel-sharing";
import { channelLabel, isDm } from "./chat-model";
import { type Channel, type ChannelMember, chatKeys, membersQuery } from "./chat-queries";
import { WebhooksTab } from "./chat-switch-b";
import { MemberPopover, MemberTrigger } from "./member-card";
import type { ChatContext } from "./use-chat";

/** `agents`: the old Agents tab (CHAT-DE folded it into Settings); it opens Settings. */
export type DetailsTab = "about" | "members" | "agents" | "settings" | "spaces" | "danger";

/** CHAT-DE: the tab a requested one opens (the retired Agents tab → Settings). */
export const openedTab = (tab: DetailsTab | string): string =>
  tab === "agents" ? "settings" : tab;

type Policy = Channel["posting_policy"];
type Notify = Channel["default_notify_level"];

const POLICY: { value: Policy; label: string }[] = [
  { value: "everyone", label: "Everyone" },
  { value: "admins", label: "Admins only" },
];
const NOTIFY: { value: Notify; label: string }[] = [
  { value: "all", label: "All messages" },
  { value: "mentions", label: "Mentions" },
  { value: "none", label: "Nothing" },
];

/** The dialog's own data: the channel row kept fresh, the mutations every tab shares. */
function useChannelEdits(chat: ChatContext, channel: Channel) {
  const queryClient = useQueryClient();
  const { api, co } = chat;
  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: chatKeys.channel(co, channel.id) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.channels(co) }),
      queryClient.invalidateQueries({ queryKey: chatKeys.members(co, channel.id) }),
    ]);
  const patch = useMutation({
    mutationFn: (body: Parameters<typeof api.client.channels.update>[0]["body"]) =>
      api.client.channels.update({ params: { co, id: channel.id }, body }),
    onSuccess: (c) => {
      queryClient.setQueryData(chatKeys.channel(co, c.id), c);
      void refresh();
      toast.add({ title: "Channel updated", type: "success" });
    },
    onError: (e) =>
      toast.add({ title: "Could not update the channel", description: e.message, type: "error" }),
  });
  return { patch, refresh };
}

/** What the viewer may do on the channel: `manage` (the API's `access.manage`). */
export type ChannelDetailsCan = { manage: boolean };

export type ChannelDetailsTabProps = {
  chat: ChatContext;
  channel: Channel;
  can: ChannelDetailsCan;
  onClose: () => void;
};

/**
 * PARITY-0: a tab of the dialog. Today's four are registrations below; a feature adds its own
 * with `registerChannelDetailsTab` (sorted by order, then id; `when` decides who sees it).
 */
export type ChannelDetailsTab = {
  id: string;
  title: string;
  /** Lower first; ties by id. */
  order: number;
  Component: ComponentType<ChannelDetailsTabProps>;
  /** Shown only when this says so (absent: always). */
  when?: (props: ChannelDetailsTabProps) => boolean;
};

const detailsTabs = orderedRegistry<ChannelDetailsTab>("channel details tab");

/** Adds a tab; throws when its id is taken. Returns the unregister function. */
export function registerChannelDetailsTab(tab: ChannelDetailsTab): () => void {
  return detailsTabs.register(tab);
}

/** Every registered tab, sorted by order, then id. */
export function channelDetailsTabs(): ChannelDetailsTab[] {
  return detailsTabs.list();
}

/** The tabs this viewer sees on this channel (each tab's `when`). */
export function visibleChannelDetailsTabs(props: ChannelDetailsTabProps): ChannelDetailsTab[] {
  return detailsTabs.list().filter((t) => !t.when || t.when(props));
}

// Settings for any channel but a DM; the Danger zone for its managers, never on a DM.
registerChannelDetailsTab({ id: "about", title: "About", order: 10, Component: AboutTab });
registerChannelDetailsTab({ id: "members", title: "Members", order: 20, Component: MembersTab });
// CHAT-DE (2026-10-27): the Agents tab (WF-FIX) is gone; the DEs are Members' rows and their
// rule is a row of Settings (`openedTab` sends the old tab there).
registerChannelDetailsTab({
  id: "settings",
  title: "Settings",
  order: 30,
  Component: SettingsTab,
  when: ({ channel }) => !isDm(channel),
});
// MULTI-SPACE-ACCESS: the spaces it is also shared into, for its managers (never a private
// channel or a DM: their members are their grant).
registerChannelDetailsTab({
  id: "spaces",
  title: "Spaces",
  order: 32,
  Component: SharingTab,
  when: ({ channel, can }) => can.manage && !isDm(channel) && !channel.is_private,
});
// CHAT-SWITCH-B: incoming webhooks, for the channel's managers (never a DM).
registerChannelDetailsTab({
  id: "integrations",
  title: "Integrations",
  order: 35,
  Component: WebhooksTab,
  when: ({ channel, can }) => can.manage && !isDm(channel),
});
registerChannelDetailsTab({
  id: "danger",
  title: "Danger zone",
  order: 40,
  Component: DangerTab,
  when: ({ channel, can }) => can.manage && !isDm(channel),
});

export function ChannelDetailsDialog({
  chat,
  channel,
  tab = "about",
  onClose,
}: {
  chat: ChatContext;
  channel: Channel;
  tab?: DetailsTab;
  onClose: () => void;
}) {
  // MULTI-SPACE-ACCESS: the list row's `can_manage` also counts (a Full access share manages).
  const listed = useManagesChannel(chat, channel, false);
  const manage = useCanManage(chat, channel) || listed;
  const dm = isDm(channel);
  const props: ChannelDetailsTabProps = { chat, channel, can: { manage }, onClose };
  const tabs = visibleChannelDetailsTabs(props);
  // A tab this channel does not show (Settings on a DM) opens About.
  const wanted = openedTab(tab);
  const [current, setCurrent] = useState<string>(
    tabs.some((t) => t.id === wanted) ? wanted : "about",
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl" data-dialog="channel-details">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {channelLabel(channel, chat)}
            {channel.is_private && !dm ? <Badge variant="neutral">Private</Badge> : null}
            {channel.archived_at ? <Badge variant="outline">Archived</Badge> : null}
          </DialogTitle>
          <DialogDescription>
            {channel.description || (dm ? "A direct message." : "No description yet.")}
          </DialogDescription>
        </DialogHeader>
        <Tabs value={current} onValueChange={(v) => setCurrent(String(v))}>
          <TabsList variant="line" className="w-full">
            {tabs.map((t) => (
              <TabsTrigger key={t.id} value={t.id}>
                {t.title}
              </TabsTrigger>
            ))}
          </TabsList>
          {tabs.map((t) => (
            <TabsContent key={t.id} value={t.id} className="pt-4">
              <t.Component {...props} />
            </TabsContent>
          ))}
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

/** About: rename and describe (managers), who made it and when, Leave (members). */
function AboutTab({ chat, channel, can, onClose }: ChannelDetailsTabProps) {
  const manage = can.manage;
  const { patch } = useChannelEdits(chat, channel);
  const [name, setName] = useState(channel.name);
  const [description, setDescription] = useState(channel.description ?? "");
  const dm = isDm(channel);
  const editable = manage && !channel.archived_at && !dm;
  const creator = channel.actor_membership_id
    ? (chat.members.get(channel.actor_membership_id)?.name ?? "A former member")
    : "CompanyOS";
  const prefs = usePrefs(chat, channel);
  const [charterOpen, setCharterOpen] = useState(false);
  const memoryOn = featureState(useSession().me, chat.co, "memory").enabled;
  const canLeave = chat.meId !== null && !chat.readOnly && !dm && !channel.is_default;
  const dirty = name.trim() !== channel.name || description.trim() !== (channel.description ?? "");
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!editable || !dirty || !name.trim()) return;
    patch.mutate({
      ...(name.trim() !== channel.name ? { name: name.trim() } : {}),
      ...(description.trim() !== (channel.description ?? "")
        ? { description: description.trim() || null }
        : {}),
    });
  };
  return (
    <form onSubmit={submit} className="flex flex-col gap-4" data-tab="about">
      {!dm ? (
        <Field>
          <FieldLabel htmlFor="channel-details-name">Name</FieldLabel>
          <Input
            id="channel-details-name"
            className="h-10"
            value={name}
            readOnly={!editable}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>
      ) : null}
      <Field>
        <FieldLabel htmlFor="channel-details-description">Description</FieldLabel>
        <Textarea
          id="channel-details-description"
          rows={2}
          value={description}
          readOnly={!editable}
          placeholder={editable ? "What this channel is for" : "No description"}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
        <dt className="text-muted-foreground">Created by</dt>
        <dd className="m-0">{creator}</dd>
        <dt className="text-muted-foreground">Created</dt>
        <dd className="m-0">{new Date(channel.created_at).toLocaleDateString()}</dd>
        <dt className="text-muted-foreground">Kind</dt>
        <dd className="m-0">
          {dm ? "Direct message" : channel.is_private ? "Private channel" : "Public channel"}
          {channel.is_default ? " · default for everyone" : ""}
        </dd>
      </dl>
      {!dm && memoryOn && channel.space_id ? (
        <div className="flex items-center gap-1">
          <Button type="button" variant="outline" size="sm" onClick={() => setCharterOpen(true)}>
            Charter
          </Button>
          <InfoTip label="About the charter">
            What this channel is for and how it works. Memory uses it to decide what belongs here.
          </InfoTip>
          {charterOpen ? (
            <CharterDialog
              api={chat.api}
              co={chat.co}
              kind="space"
              id={channel.space_id}
              name={channel.name}
              open
              onClose={() => setCharterOpen(false)}
            />
          ) : null}
        </div>
      ) : null}
      <div className="flex items-center justify-between gap-2 pt-1">
        {canLeave ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-danger-text hover:bg-danger-soft"
            onClick={() =>
              prefs.save.mutate(
                { joined: false },
                {
                  onSuccess: () => {
                    toast.add({ title: `Left ${channelLabel(channel, chat)}`, type: "success" });
                    onClose();
                  },
                },
              )
            }
          >
            <LogOutIcon data-icon="inline-start" />
            Leave channel
          </Button>
        ) : (
          <span />
        )}
        {editable ? (
          <Button type="submit" size="sm" disabled={!dirty || !name.trim() || patch.isPending}>
            Save changes
          </Button>
        ) : null}
      </div>
    </form>
  );
}

/** Members: the rows named through the company's people, add, remove, promote or demote. */
function MembersTab({ chat, channel, can }: ChannelDetailsTabProps) {
  // The API's own answer (space owners manage too), as the old Agents tab read it.
  const manage = useManagesChannel(chat, channel, can.manage);
  const { api, co } = chat;
  const { refresh } = useChannelEdits(chat, channel);
  const members = useQuery(membersQuery(api, co, channel.id));
  const dm = isDm(channel);
  const canWrite = chat.meId !== null && !chat.readOnly && !channel.archived_at && !dm;
  const rows = (members.data?.data ?? [])
    .map((m) => ({ row: m, who: chat.members.get(m.membership_id) }))
    .sort(
      (a, b) =>
        Number(b.row.role === "admin") - Number(a.row.role === "admin") ||
        (a.who?.name ?? "").localeCompare(b.who?.name ?? ""),
    );
  const remove = useMutation({
    mutationFn: (membership_id: string) =>
      api.client.channels.removeMember({ params: { co, id: channel.id, membership_id } }),
    onSuccess: () => void refresh(),
    onError: (e) =>
      toast.add({ title: "Could not remove the member", description: e.message, type: "error" }),
  });
  const role = useMutation({
    mutationFn: (v: { membership_id: string; role: ChannelMember["role"] }) =>
      api.client.channels.updateMember({
        params: { co, id: channel.id, membership_id: v.membership_id },
        body: { role: v.role },
      }),
    onSuccess: (r) => {
      void refresh();
      toast.add({
        title: `${chat.members.get(r.membership_id)?.name ?? "The member"} is ${r.role === "admin" ? "now a channel admin" : "a member again"}`,
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({ title: "Could not change the role", description: e.message, type: "error" }),
  });
  return (
    <div className="flex flex-col gap-3" data-tab="members">
      <div className="flex items-center justify-between gap-2">
        {members.isError ? (
          <p role="alert" className="m-0 flex items-center gap-2 text-xs text-muted-foreground">
            Could not load the members.
            <Button
              type="button"
              variant="secondary"
              size="xs"
              onClick={() => void members.refetch()}
            >
              Retry
            </Button>
          </p>
        ) : (
          <p className="m-0 text-xs text-muted-foreground">
            {members.isSuccess
              ? `${rows.length} ${rows.length === 1 ? "member" : "members"}`
              : "Loading…"}
          </p>
        )}
        {/* CHAT-DE: one search across people and DEs (where the API lets the caller add). */}
        {canWrite ? <AddPeopleButton chat={chat} channel={channel} variant="secondary" /> : null}
      </div>
      {chat.deDirectory.failed ? (
        // A DE page failed: which members are DEs is not known; say so (never "no DE here").
        <p role="alert" className="m-0 flex items-center gap-2 text-xs text-muted-foreground">
          Could not load the digital employees.
          <Button type="button" variant="secondary" size="xs" onClick={chat.deDirectory.retry}>
            Retry
          </Button>
        </p>
      ) : null}
      {chat.peopleDirectory.failed ? (
        // WF-FIX (2026-10-26): a page of people failed: the picker misses some, say so.
        <p role="alert" className="m-0 flex items-center gap-2 text-xs text-muted-foreground">
          Could not load everyone in the company.
          <Button type="button" variant="secondary" size="xs" onClick={chat.peopleDirectory.retry}>
            Retry
          </Button>
        </p>
      ) : null}
      <ul className="m-0 flex max-h-72 list-none flex-col overflow-y-auto p-0">
        {rows.map(({ row, who }) => {
          const me = row.membership_id === chat.meId;
          const de = who?.kind === "de";
          const name = who ? (de ? workerName(who.name) : who.name) : "Former member";
          const face = (
            <>
              <AvatarTile name={name} kind={de ? "agent" : "person"} size="xs" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">
                {name}
                {me ? " (you)" : ""}
              </span>
            </>
          );
          return (
            <li
              key={row.membership_id}
              data-member-row={row.membership_id}
              className="group/member flex items-center gap-2.5 py-1.5 text-sm"
            >
              {/* CHAT-DM: the name opens the member's profile popover ("Message"). */}
              <MemberPopover
                chat={chat}
                memberId={row.membership_id}
                trigger={
                  <MemberTrigger
                    label={`${name}: profile`}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-sm border-0 bg-transparent p-0 text-left text-inherit hover:underline focus-visible:outline-2 focus-visible:outline-ring"
                  >
                    {face}
                  </MemberTrigger>
                }
                fallback={<span className="flex min-w-0 flex-1 items-center gap-2.5">{face}</span>}
              />
              {de ? <WorkerBadge kind="de" size="sm" /> : null}
              {row.role === "admin" ? <Badge variant="outline">Admin</Badge> : null}
              {canWrite && manage ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="xs"
                  className="text-secondary opacity-0 group-hover/member:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                  aria-label={`${row.role === "admin" ? "Remove admin from" : "Make admin"}: ${who?.name ?? "member"}`}
                  disabled={role.isPending}
                  onClick={() =>
                    role.mutate({
                      membership_id: row.membership_id,
                      role: row.role === "admin" ? "member" : "admin",
                    })
                  }
                >
                  {row.role === "admin" ? (
                    <ShieldOffIcon data-icon="inline-start" />
                  ) : (
                    <ShieldCheckIcon data-icon="inline-start" />
                  )}
                  {row.role === "admin" ? "Remove admin" : "Make admin"}
                </Button>
              ) : null}
              {/* A default channel keeps its people; its managers still remove a DE (as the
                  Agents tab let them). */}
              {canWrite && (((manage || me) && !channel.is_default) || (manage && de)) ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  className="text-faint opacity-0 hover:bg-danger-soft hover:text-danger-text group-hover/member:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100"
                  aria-label={`Remove ${who ? name : "member"}`}
                  title="Remove from channel"
                  disabled={remove.isPending}
                  onClick={() => remove.mutate(row.membership_id)}
                >
                  <UserMinusIcon />
                </Button>
              ) : null}
            </li>
          );
        })}
        {members.isSuccess && !rows.length ? (
          <li className="py-2 text-sm text-muted-foreground">No members listed yet.</li>
        ) : null}
      </ul>
    </div>
  );
}

/** Settings and permissions: who posts, who opens topics, private, the default notifications. */
function SettingsTab({ chat, channel, can }: ChannelDetailsTabProps) {
  const { patch } = useChannelEdits(chat, channel);
  const editable = can.manage && !channel.archived_at && !chat.readOnly;
  // The API's own answer for the agents' rule (space owners manage too), not the role guess.
  const managesRules = useManagesChannel(chat, channel, can.manage);
  // A default channel is everyone's (05, CHAT-UX); a team channel follows its team: neither
  // turns private. Agent rooms keep their flag (the API refuses the change).
  const privacyLocked =
    channel.is_default || channel.kind === "team" || channel.kind === "board_home";
  return (
    <div className="flex flex-col gap-5" data-tab="settings">
      <Field>
        <FieldLabel>Who can post</FieldLabel>
        <Segmented
          aria-label="Who can post"
          options={POLICY}
          value={channel.posting_policy}
          onValueChange={(v) => editable && patch.mutate({ posting_policy: v })}
        />
        <FieldDescription>
          {channel.posting_policy === "admins"
            ? "Only the channel's admins and the space's owners post; everyone else reads."
            : "Everyone who can see the channel posts in it."}
        </FieldDescription>
      </Field>
      <Field>
        <FieldLabel>Who can create topics</FieldLabel>
        <Segmented
          aria-label="Who can create topics"
          options={POLICY}
          value={channel.topic_policy}
          onValueChange={(v) => editable && patch.mutate({ topic_policy: v })}
        />
        <FieldDescription>
          {channel.topic_policy === "admins"
            ? "Admins open topics; everyone posts in the ones that exist."
            : "Anyone who posts can open a topic."}
        </FieldDescription>
      </Field>
      {/* CHAT-SWITCH-A: Slack's "who can use @channel and @here". */}
      <Field>
        <FieldLabel>Who can use @channel and @here</FieldLabel>
        <Segmented
          aria-label="Who can use @channel and @here"
          options={POLICY}
          value={channel.broadcast_policy}
          onValueChange={(v) => editable && patch.mutate({ broadcast_policy: v })}
        />
        <FieldDescription>
          {channel.broadcast_policy === "admins"
            ? "Only admins can notify the whole channel."
            : "Anyone who posts can notify the whole channel."}
        </FieldDescription>
      </Field>
      <Field orientation="horizontal">
        <Switch
          id="channel-details-private"
          checked={channel.is_private}
          disabled={!editable || privacyLocked}
          onCheckedChange={(v) => editable && patch.mutate({ is_private: Boolean(v) })}
        />
        <FieldLabel htmlFor="channel-details-private">
          Private: members only
          {privacyLocked ? (
            <span className="ml-1 text-xs font-normal text-muted-foreground">
              (a {channel.is_default ? "default" : channel.kind === "team" ? "team" : "board"}{" "}
              channel stays {channel.is_private ? "private" : "public"})
            </span>
          ) : null}
        </FieldLabel>
      </Field>
      <Field>
        <FieldLabel>Default notifications for new members</FieldLabel>
        <Segmented
          aria-label="Default notifications for new members"
          options={NOTIFY}
          value={channel.default_notify_level}
          onValueChange={(v) => editable && patch.mutate({ default_notify_level: v })}
        />
        <FieldDescription>
          What a person gets when they join; each member changes their own from the bell.
        </FieldDescription>
      </Field>
      <AgentRulesRow
        chat={chat}
        channel={channel}
        editable={managesRules && chat.meId !== null && !chat.readOnly && !channel.archived_at}
      />
      {!editable ? (
        <p className="m-0 text-xs text-muted-foreground">
          {channel.archived_at
            ? "The channel is archived: restore it to change its settings."
            : "Only the channel's admins change these."}
        </p>
      ) : null}
    </div>
  );
}

/** Danger zone (managers): archive or restore; delete with the name typed back. */
function DangerTab({ chat, channel, onClose }: ChannelDetailsTabProps) {
  const { api, co } = chat;
  const { refresh } = useChannelEdits(chat, channel);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState("");
  const archived = channel.archived_at !== null;
  const archive = useMutation({
    mutationFn: () =>
      archived
        ? api.client.channels.unarchive({ params: { co, id: channel.id } })
        : api.client.channels.archive({ params: { co, id: channel.id } }),
    onSuccess: (c) => {
      queryClient.setQueryData(chatKeys.channel(co, c.id), c);
      void refresh();
      toast.add({
        title: `${archived ? "Restored" : "Archived"} ${channelLabel(channel, chat)}`,
        type: "success",
      });
    },
    onError: (e) =>
      toast.add({
        title: `Could not ${archived ? "restore" : "archive"} the channel`,
        description: e.message,
        type: "error",
      }),
  });
  const remove = useMutation({
    mutationFn: () =>
      api.client.channels.delete({
        params: { co, id: channel.id },
        query: { confirm: typed.trim() },
      }),
    onSuccess: async () => {
      queryClient.removeQueries({ queryKey: chatKeys.channel(co, channel.id) });
      await refresh();
      toast.add({ title: `Deleted ${channelLabel(channel, chat)}`, type: "success" });
      setConfirming(false);
      onClose();
      void navigate({
        to: "/c/$slug/chat",
        params: { slug: chat.company.slug },
        search: keepFocus as never,
      });
    },
    onError: (e) =>
      toast.add({ title: "Could not delete the channel", description: e.message, type: "error" }),
  });
  const undeletable = channel.is_default || channel.kind === "board_home";
  const matches = typed.trim().toLowerCase() === channel.name.toLowerCase();
  return (
    <div className="flex flex-col gap-4" data-tab="danger">
      <div className="flex items-start justify-between gap-4 rounded-md border border-border-subtle p-3">
        <div className="min-w-0">
          <p className="m-0 text-sm font-medium text-foreground">
            {archived ? "Restore channel" : "Archive channel"}
          </p>
          <p className="m-0 text-xs text-muted-foreground">
            {archived
              ? "Posting resumes and the channel is back in everyone's sidebar."
              : "Read-only from then on; nothing is deleted. Restore it any time from Archived channels."}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={archive.isPending || (channel.is_default && !archived)}
          onClick={() => archive.mutate()}
        >
          {archived ? (
            <ArchiveRestoreIcon data-icon="inline-start" />
          ) : (
            <ArchiveIcon data-icon="inline-start" />
          )}
          {archived ? "Restore" : "Archive"}
        </Button>
      </div>
      <div className="flex items-start justify-between gap-4 rounded-md border border-danger-text/30 p-3">
        <div className="min-w-0">
          <p className="m-0 text-sm font-medium text-danger-text">Delete channel</p>
          <p className="m-0 text-xs text-muted-foreground">
            {undeletable
              ? `A ${channel.is_default ? "default" : "board"} channel cannot be deleted; archive it instead.`
              : "The channel disappears for everyone. Its messages are kept, never served again (nothing is hard-deleted)."}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={undeletable}
          onClick={() => setConfirming(true)}
        >
          <Trash2Icon data-icon="inline-start" />
          Delete
        </Button>
      </div>
      {confirming ? (
        <AlertDialog open onOpenChange={(o) => !o && !remove.isPending && setConfirming(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {channelLabel(channel, chat)}?</AlertDialogTitle>
              <AlertDialogDescription>
                Type the channel's name to confirm. Everyone loses it from their sidebar; its
                messages are kept but never shown again.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Field>
              <FieldLabel htmlFor="channel-delete-confirm">Channel name</FieldLabel>
              <Input
                id="channel-delete-confirm"
                className="h-10"
                placeholder={channel.name}
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
              />
            </Field>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                variant="destructive"
                disabled={!matches || remove.isPending}
                onClick={() => remove.mutate()}
              >
                Delete channel
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ) : null}
    </div>
  );
}
